import { registerLocaleData } from '@angular/common';
import localeJa from '@angular/common/locales/ja';
import { Component, HostListener } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { take } from 'rxjs';

import { ROUTER_CONFIG, SESSION_STORAGE_KEY, TRIGGER_SESSION_KEY } from '@careconne/core/configs';
import { AppHelper } from '@careconne/core/helper';
import { AuthService } from '@careconne/core/services/apis';
import {
  AutoLogoutService,
  LocalStorageService,
  MultipleSpinnerService,
  SessionStorageService,
} from '@careconne/core/services/common';

@Component({
  selector: 'dbs-app',
  template: '<router-outlet></router-outlet>',
})
export class AppComponent {
  private readonly skipCheckTokenRoutes = [ROUTER_CONFIG.download];

  constructor(
    private multipleSpinnerService: MultipleSpinnerService,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private matDialog: MatDialog,
    private autoLogoutService: AutoLogoutService,
    private router: Router,
    private authService: AuthService,
    private sessionStorageService: SessionStorageService,
    private localStorageService: LocalStorageService,
  ) {
    this.multipleSpinnerService.trackRouteLoadIndicator();

    // CUSTOM_ICON
    this.matIconRegistry.addSvgIconResolver((name, namespace) => {
      const iconPath = './assets/icons' + (namespace ? '/' + namespace : '') + '/' + name + '.svg';
      return this.domSanitizer.bypassSecurityTrustResourceUrl(iconPath);
    });

    registerLocaleData(localeJa, 'ja-JP');

    this.shareSession();

    setTimeout(async () => {
      this.authService.hasQueryToken.pipe(take(1)).subscribe(async (hasQueryToken) => {
        if (!hasQueryToken) {
          const skipCheckToken = this.skipCheckTokenRoutes.some((v) => AppHelper.isRouteActive([v], this.router));
          if (skipCheckToken) {
            return;
          }

          const session = this.sessionStorageService.getItem(SESSION_STORAGE_KEY.sessionLogin);
          const token = this.localStorageService.getAccessToken();
          if (!session && token) {
            const isAccessTokenExpired = this.authService.isAccessTokenExpired;
            if (isAccessTokenExpired) {
              this.router.navigate([ROUTER_CONFIG.auth.login]);
              return;
            }
            await this.authService.logout();
          }
        } else {
          this.authService.setHasQueryToken(false);
        }
      });
    }, 1500);
  }

  shareSession() {
    // ask other tabs for session storage (this is ONLY to trigger event)
    if (!this.sessionStorageService.getItem(SESSION_STORAGE_KEY.sessionLogin)) {
      this.localStorageService.trigger(TRIGGER_SESSION_KEY.getSessionStorage);
    }
  }

  @HostListener('window:storage', ['$event'])
  onStorage(event: StorageEvent) {
    if (!event.newValue) {
      return;
    } // do nothing if no value to work with
    if (event.key === TRIGGER_SESSION_KEY.getSessionStorage) {
      // another tab asked for the sessionStorage -> send it
      // the other tab should now have it, so we're done with it.
      this.localStorageService.trigger(TRIGGER_SESSION_KEY.sessionStorage, {
        [SESSION_STORAGE_KEY.sessionLogin]: this.sessionStorageService.getItem(SESSION_STORAGE_KEY.sessionLogin),
      });
    } else if (
      event.key === TRIGGER_SESSION_KEY.sessionStorage &&
      !this.sessionStorageService.getItem(SESSION_STORAGE_KEY.sessionLogin)
    ) {
      const data = JSON.parse(event.newValue);
      if (data) {
        for (const [key, value] of Object.entries<any>(data)) {
          this.sessionStorageService.setItem(key, value);
        }
      }
    } else if (event.key === TRIGGER_SESSION_KEY.removeToken) {
      this.matDialog.closeAll();
      this.autoLogoutService.stop();
      this.router
        .navigate([ROUTER_CONFIG.auth.login])
        .then(() => this.sessionStorageService.removeItems([SESSION_STORAGE_KEY.sessionLogin]));
    } else if (event.key === TRIGGER_SESSION_KEY.setToken) {
      setTimeout(() => {
        this.router.navigate([ROUTER_CONFIG.home]);
      }, 500);
    } else if (event.key === TRIGGER_SESSION_KEY.isActive) {
      this.autoLogoutService.reset();
    }
  }

  @HostListener('document:mousemove', ['$event'])
  @HostListener('document:mousedown', ['$event'])
  @HostListener('document:mousewheel', ['$event'])
  @HostListener('document:wheel', ['$event'])
  @HostListener('document:keydown', ['$event'])
  @HostListener('document:touchstart', ['$event'])
  @HostListener('document:touchmove', ['$event'])
  onActive() {
    this.autoLogoutService.reset();
    this.localStorageService.trigger(TRIGGER_SESSION_KEY.isActive);
  }
}
